/**
 * Turns the constructs whose visible text Word draws from its own markup — a
 * complex field (REF, PAGEREF, SEQ, TOC), a `w:fldSimple` (DATE, PAGE,
 * NUMPAGES), an equation — into ordinary runs, so a tracked change written
 * over that text is the document's own text.
 *
 * WHY
 *   Word redraws a field's result whenever fields update: F9, printing, and
 *   opening the file for DATE and for a table of contents. A tracked change
 *   written into a live field's result is thrown away at that moment and the
 *   user watches their redline disappear. Unlinking is Word's own
 *   Ctrl+Shift+F9: the result text stays exactly where it is, the field that
 *   owned it goes, and those words stop updating with the document. An
 *   equation is never recomputed by Word, but its characters live in math runs
 *   (`m:r`), which carry no tracked-change spelling the splicer can write, so
 *   the same pass flattens it to ordinary runs.
 *
 * INVARIANT
 *   Unlinking never changes the paragraph's accepted text by one character.
 *   Field markers and instruction text hold no accepted characters; a
 *   `w:fldSimple`'s runs are moved rather than rebuilt; an equation's math runs
 *   become ordinary runs holding the same characters in the same order. The
 *   engine measured its planned edits against that text before this pass and
 *   applies them after it without replanning.
 *
 * WHAT IS LEFT ALONE
 *   Only the constructs the planned edits actually reach. A cross-reference
 *   elsewhere in the same paragraph stays a cross-reference and goes on
 *   updating. A HYPERLINK field is never unlinked, in either spelling: Word
 *   keeps its display text when fields update (`fieldResultIsRedrawnByWord`),
 *   so a tracked change in it stands, and unlinking it only lost the link.
 *
 * THE ONE FIELD WALK
 *   `complexFieldWalkOf` is the one reading of a paragraph's complex fields:
 *   which runs each field holds, which of them are its markers and its result,
 *   what its instruction says, and which fields are open over each run. The
 *   paragraph model's editability, this unlinker, the link striker and the
 *   splicer's restore of struck runs all read that one walk, so no two of them
 *   can disagree about which field a run is in.
 */
import {
    elementNameWithoutPrefix,
    FIELD_CHAR_TAG,
    FIELD_CHAR_TYPES,
    FIELD_INSTRUCTION_TAGS,
    fieldResultIsRedrawnByWord,
    fieldSimpleInstructionOf,
    isGlyphTag,
    OPAQUE_TAGS,
    PROPERTY_TAGS,
    REVISION_CONTAINERS,
    TEXT_TAGS,
    WORDPROCESSINGML_NS,
    XML_NS,
} from "./ooxml-names";
import type { ParagraphModel } from "./paragraph-model";

/** A stretch of the paragraph's accepted text: `startOffset` inclusive, `endOffset` exclusive, the two equal for an insertion point. */
export interface AcceptedTextRange {
    startOffset: number;
    endOffset: number;
}

/** One complex field of a paragraph as the one field walk reads it (`complexFieldWalkOf`). */
export interface ComplexField {
    /** Every run from its `fldChar begin` to its `fldChar end`, both included, nested fields' runs among them; up to the paragraph's last run for a field that ends in a later paragraph. */
    runsBeginToEnd: Element[];
    /** Its begin run, its instruction runs, its separate run and its end run: what unlinking removes, and what a restore leaves out of a field that does not come back whole. */
    markerRuns: Element[];
    /** The runs after its `separate` and before its `end`, nested fields' runs among them. */
    resultRuns: Element[];
    /** The words of its own instruction runs, read before its `separate`: ' REF _Ref123 \h ', ' HYPERLINK "https://x.example/fee" \h '. */
    fieldInstruction: string;
    /** True once its `end` was met in this paragraph after its `begin`; false for a field that ends in a later paragraph or began in an earlier one. */
    complete: boolean;
}

/** A field open over a run, and whether that run lies in the field's result (after its `separate`) rather than its instruction part. */
export interface FieldOpenOverRun {
    field: ComplexField;
    inResult: boolean;
}

/** What the one walk over a paragraph's runs found (`complexFieldWalkOf`). */
export interface ComplexFieldWalk {
    /** Every field with a marker in the paragraph: complete fields in the order their `end` was met, the tail of an earlier paragraph's field where its `end` was met, then the fields still open at the paragraph's end. */
    fields: ComplexField[];
    /** The fields open over each run, outermost first; a run outside every field is absent. A marker run is inside the field it marks, in the state its marker leaves. */
    fieldsOpenOverRun: Map<Element, FieldOpenOverRun[]>;
    /** For every begin, instruction, separate and end run, the field it marks; the tail of an earlier paragraph's field included. */
    fieldOfMarkerRun: Map<Element, ComplexField>;
}

/** The nested field spelling: instruction in an attribute, result runs as children. */
const FIELD_SIMPLE_TAG = "fldSimple";
/** A `w:fldSimple` child holding the field's own binary data, which is not text and goes with the field. */
const FIELD_DATA_TAG = "fldData";
/** The math arguments whose runs Word draws raised and lowered, kept as character formatting when the equation is flattened. */
const MATH_SUPERSCRIPT_ARGUMENT_TAG = "sup";
const MATH_SUBSCRIPT_ARGUMENT_TAG = "sub";
/** `w:vertAlign` values for those two arguments. */
const SUPERSCRIPT_ALIGNMENT = "superscript";
const SUBSCRIPT_ALIGNMENT = "subscript";
type MathVerticalAlignment = typeof SUPERSCRIPT_ALIGNMENT | typeof SUBSCRIPT_ALIGNMENT;

export class WordOwnedTextUnlinker {
    /**
     * Unlinks every construct whose text the given accepted-text ranges reach
     * and refreshes the model when it changed anything. Returns how many
     * constructs were unlinked, so a caller can tell an untouched paragraph
     * from one whose fields are now ordinary text.
     *
     * CASES
     *   - no range reaches uneditable text  -> 0, the paragraph is untouched
     *   - a range reaches a field's result  -> that field's markers go, its result text stays
     *   - a range reaches an equation       -> the equation becomes runs of the same characters
     *   - a range reaches text inside both  -> both go, outermost construct first
     */
    static unlinkConstructsReachedBy(paragraphModel: ParagraphModel, ranges: readonly AcceptedTextRange[]): number {
        const runsHoldingReachedText = WordOwnedTextUnlinker.runsHoldingTextReachedBy(paragraphModel, ranges);
        /**
         * WHEN no reached character sits in text Word draws
         *   CONSEQUENCE
         *     nothing to unlink: the ordinary splice path writes the tracked change.
         */
        if (runsHoldingReachedText.length === 0) return 0;
        const paragraph = paragraphModel.paragraph;
        const fieldWalk = complexFieldWalkOf(paragraph);
        const opaqueElementsOutermostFirst: Element[] = [];
        const markerRunsToRemove: Element[] = [];
        const unlinkedFlatFields: ComplexField[] = [];
        for (const run of runsHoldingReachedText) {
            for (const opaqueElement of WordOwnedTextUnlinker.opaqueAncestorsOutermostFirst(run, paragraph)) {
                /**
                 * WHEN two reached runs sit in the same equation or `w:fldSimple`
                 *   CONSEQUENCE
                 *     it is unlinked once.
                 */
                if (!opaqueElementsOutermostFirst.includes(opaqueElement)) opaqueElementsOutermostFirst.push(opaqueElement);
            }
            /**
             * Every field open over the run whose result Word redraws, outermost first: a run
             * inside a cross-reference that sits inside a table of contents belongs to both,
             * and editing its text has to unlink both, or the outer field redraws the inner
             * one away. A HYPERLINK open over it is the author's text and is left as it is.
             */
            for (const { field } of fieldWalk.fieldsOpenOverRun.get(run) ?? []) {
                if (!fieldResultIsRedrawnByWord(field.fieldInstruction)) continue;
                /**
                 * WHEN two reached runs sit in the same complex field
                 *   CONSEQUENCE
                 *     its markers are collected once, and it counts as one unlinked construct.
                 */
                if (unlinkedFlatFields.includes(field)) continue;
                unlinkedFlatFields.push(field);
                markerRunsToRemove.push(...field.markerRuns);
            }
        }
        for (const opaqueElement of opaqueElementsOutermostFirst) {
            /**
             * WHEN an outer construct already carried this one away
             *   CONSEQUENCE
             *     skip it: an equation flattened as part of an outer equation is no longer in the paragraph.
             */
            if (!WordOwnedTextUnlinker.sitsInside(opaqueElement, paragraph)) continue;
            /**
             * CASES, by which spelling the construct uses
             *   - `w:fldSimple`          -> its runs move up and the element goes
             *   - `m:oMath`/`m:oMathPara` -> its math runs become ordinary runs holding the same characters
             */
            if (elementNameWithoutPrefix(opaqueElement) === FIELD_SIMPLE_TAG) WordOwnedTextUnlinker.unwrapFieldSimple(opaqueElement);
            else WordOwnedTextUnlinker.flattenMath(opaqueElement);
        }
        for (const markerRun of markerRunsToRemove) WordOwnedTextUnlinker.removeFieldMarkers(markerRun);
        const unlinkedConstructCount = opaqueElementsOutermostFirst.length + unlinkedFlatFields.length;
        /**
         * WHEN any construct was unlinked
         *   CONSEQUENCE
         *     re-walk the paragraph so the pieces the splicer is about to cut are the new,
         *     editable ones. The accepted text is the same text it was, so the planned
         *     offsets still name the same characters.
         */
        if (unlinkedConstructCount > 0) paragraphModel.refresh();
        return unlinkedConstructCount;
    }

    /**
     * The runs holding accepted text that the ranges reach and that the model
     * marked as Word's own, in document order, each run once.
     *
     * EXAMPLE
     *   accepted text  = "The cap is set out in Section 2.1, and it survives."
     *   field result   = piece 22-33 "Section 2.1", uneditable
     *   range 22-33 (replace the reference)     -> that piece's run is reached
     *   range 4-7  (replace "cap")              -> no uneditable piece is reached, []
     *   range 27-27 (insert inside "Section")   -> reached: the insertion point sits inside the piece
     */
    private static runsHoldingTextReachedBy(paragraphModel: ParagraphModel, ranges: readonly AcceptedTextRange[]): Element[] {
        const runs: Element[] = [];
        for (const piece of paragraphModel.acceptedPieces()) {
            /**
             * WHEN the piece is ordinary document text
             *   CONSEQUENCE
             *     nothing to unlink for it, whatever reaches it.
             */
            if (piece.editable) continue;
            const reached = ranges.some((range) => {
                /**
                 * CASES
                 *   - a range that deletes or replaces characters -> the piece is reached when the two spans overlap
                 *   - a range that only inserts (start === end)   -> reached only when the insertion point sits
                 *                                                    strictly inside the piece, so text placed at a
                 *                                                    field's edge stays outside the field
                 */
                if (range.endOffset > range.startOffset) return piece.startOffset < range.endOffset && piece.endOffset > range.startOffset;
                return piece.startOffset < range.startOffset && piece.endOffset > range.startOffset;
            });
            if (!reached) continue;
            const run = piece.run;
            if (!runs.includes(run)) runs.push(run);
        }
        return runs;
    }

    /**
     * The `w:fldSimple`, `m:oMath` and `m:oMathPara` elements between the run
     * and the paragraph, outermost first, so an equation inside a field is
     * unlinked from the outside in and the inner element is still in the
     * paragraph when its turn comes.
     */
    private static opaqueAncestorsOutermostFirst(run: Element, paragraph: Element): Element[] {
        const ancestorsInnermostFirst: Element[] = [];
        for (let ancestor: Element | null = run.parentElement; ancestor && ancestor !== paragraph; ancestor = ancestor.parentElement) {
            /**
             * WHEN the ancestor is a `w:fldSimple` whose field Word does not redraw (a HYPERLINK)
             *   CONSEQUENCE
             *     it stays: its result is the author's text, and the link goes on working.
             */
            if (OPAQUE_TAGS.has(elementNameWithoutPrefix(ancestor)) && fieldResultIsRedrawnByWord(fieldSimpleInstructionOf(ancestor))) ancestorsInnermostFirst.push(ancestor);
        }
        return ancestorsInnermostFirst.reverse();
    }

    /** Whether the element is still a descendant of the paragraph, false once an outer construct took it out of the tree. */
    private static sitsInside(element: Element, paragraph: Element): boolean {
        for (let ancestor: Element | null = element.parentElement; ancestor; ancestor = ancestor.parentElement) {
            if (ancestor === paragraph) return true;
        }
        return false;
    }

    /** Every run of the paragraph in document order, inside revision containers, hyperlinks, content controls, fields and equations alike. */
    static runsInDocumentOrder(paragraph: Element): Element[] {
        const runs: Element[] = [];
        const collectFrom = (element: Element): void => {
            for (const child of Array.from(element.children)) {
                /**
                 * WHEN the child is a run
                 *   CONSEQUENCE
                 *     take it and do not descend: a run holds no run.
                 */
                if (elementNameWithoutPrefix(child) === "r") {
                    runs.push(child);
                    continue;
                }
                collectFrom(child);
            }
        };
        collectFrom(paragraph);
        return runs;
    }

    /**
     * The field marker this run carries, read in both spellings Word writes it
     * in, or null for a run that is not a marker.
     *
     * FALLBACK
     *   - namespace-aware attribute present -> use it
     *   - otherwise                         -> the prefixed `w:fldCharType`, which is what a
     *                                          paragraph parsed out of the document carries
     */
    static fieldCharTypeOf(run: Element): string | null {
        for (const child of Array.from(run.children)) {
            if (elementNameWithoutPrefix(child) !== FIELD_CHAR_TAG) continue;
            return child.getAttributeNS(WORDPROCESSINGML_NS, "fldCharType") ?? child.getAttribute("w:fldCharType");
        }
        return null;
    }

    /** Whether the run carries a field's instruction text rather than document text. */
    static holdsFieldInstruction(run: Element): boolean {
        return Array.from(run.children).some((child) => FIELD_INSTRUCTION_TAGS.has(elementNameWithoutPrefix(child)));
    }

    /**
     * Takes the field markers out of one marker run.
     *
     * CASES
     *   - the run holds only markers (what Word writes) -> the run goes
     *   - the run also holds text                       -> only the marker children go, so no
     *                                                      character of the paragraph is lost
     */
    private static removeFieldMarkers(markerRun: Element): void {
        const parent = markerRun.parentElement;
        /**
         * WHEN an outer construct already took this run out of the tree
         *   CONSEQUENCE
         *     nothing to remove.
         */
        if (!parent) return;
        const markerChildren: Element[] = [];
        let holdsText = false;
        for (const child of Array.from(markerRun.children)) {
            const tag = elementNameWithoutPrefix(child);
            if (tag === FIELD_CHAR_TAG || FIELD_INSTRUCTION_TAGS.has(tag)) markerChildren.push(child);
            if (TEXT_TAGS.has(tag) || isGlyphTag(tag)) holdsText = true;
        }
        if (!holdsText) {
            parent.removeChild(markerRun);
            return;
        }
        for (const markerChild of markerChildren) markerRun.removeChild(markerChild);
    }

    /**
     * Moves a `w:fldSimple`'s content out into the paragraph in its place and
     * drops the element. The runs themselves are moved, not rebuilt, so their
     * formatting, their revision containers and their comment anchors are the
     * ones the document already had.
     *
     * CASES
     *   - a `w:fldSimple` nested in this one -> unwrapped first, so its runs move up with the rest
     *   - a `w:fldData` child                -> dropped: it is the field's own data, not document text
     */
    private static unwrapFieldSimple(fieldSimpleElement: Element): void {
        const parent = fieldSimpleElement.parentElement;
        if (!parent) return;
        for (const child of Array.from(fieldSimpleElement.children)) {
            if (elementNameWithoutPrefix(child) === FIELD_SIMPLE_TAG) WordOwnedTextUnlinker.unwrapFieldSimple(child);
        }
        for (const child of Array.from(fieldSimpleElement.children)) {
            if (elementNameWithoutPrefix(child) === FIELD_DATA_TAG) continue;
            parent.insertBefore(child, fieldSimpleElement);
        }
        parent.removeChild(fieldSimpleElement);
    }

    /**
     * Replaces an equation with ordinary runs holding the same characters in
     * the same order, raised and lowered text kept as `w:vertAlign` so
     * `P(1 + r)` with an `n` above it still reads as a power.
     *
     * EXAMPLE
     *   <m:oMath><m:r><m:t>C = </m:t></m:r><m:sSup><m:e><m:r><m:t>P(1 + r)</m:t></m:r></m:e>
     *            <m:sup><m:r><m:t>n</m:t></m:r></m:sup></m:sSup></m:oMath>
     *   -> <w:r><w:t xml:space="preserve">C = </w:t></w:r>
     *      <w:r><w:t xml:space="preserve">P(1 + r)</w:t></w:r>
     *      <w:r><w:rPr><w:vertAlign w:val="superscript"/></w:rPr><w:t xml:space="preserve">n</w:t></w:r>
     *   accepted text before and after: "C = P(1 + r)n"
     */
    private static flattenMath(mathElement: Element): void {
        const parent = mathElement.parentElement;
        if (!parent) return;
        for (const plainNode of WordOwnedTextUnlinker.plainNodesForMathContent(mathElement, null)) parent.insertBefore(plainNode, mathElement);
        parent.removeChild(mathElement);
    }

    /**
     * The ordinary nodes one equation element's children become, in document
     * order, under the vertical alignment inherited from the math arguments
     * already entered.
     *
     * CASES
     *   - a run (`m:r`, or a `w:r` sitting in the equation) -> one ordinary run of its characters
     *   - a revision container (`w:ins`, `w:del`, …)        -> the same container, rebuilt shallow, over the
     *                                                          nodes its children become, so text another
     *                                                          author struck stays struck
     *   - the `m:sup` / `m:sub` argument                    -> its content, raised or lowered
     *   - any other math element (`m:sSup`, `m:f`, `m:e`, …) -> its content, alignment unchanged
     *   - a properties block (`m:rPr`, `m:ctrlPr`, …)       -> nothing: it holds no characters
     */
    private static plainNodesForMathContent(mathElement: Element, verticalAlignment: MathVerticalAlignment | null): Element[] {
        const plainNodes: Element[] = [];
        for (const child of Array.from(mathElement.children)) {
            const tag = elementNameWithoutPrefix(child);
            if (PROPERTY_TAGS.has(tag) || tag === "ctrlPr" || tag === "argPr") continue;
            if (tag === "r") {
                const plainRun = WordOwnedTextUnlinker.plainRunFor(child, verticalAlignment);
                if (plainRun) plainNodes.push(plainRun);
                continue;
            }
            if (REVISION_CONTAINERS.has(tag)) {
                const container = WordOwnedTextUnlinker.shallowWordCopyOf(child);
                for (const plainNode of WordOwnedTextUnlinker.plainNodesForMathContent(child, verticalAlignment)) container.appendChild(plainNode);
                /**
                 * WHEN the container's math content held no characters
                 *   CONSEQUENCE
                 *     leave it out rather than write an empty revision into the paragraph.
                 */
                if (container.childNodes.length > 0) plainNodes.push(container);
                continue;
            }
            /**
             * CASES, by which math argument this is
             *   - `m:sup` -> its runs are drawn raised, so they carry superscript out
             *   - `m:sub` -> its runs are drawn lowered, so they carry subscript out
             *   - anything else -> whatever alignment we are already under
             */
            const alignmentInsideChild =
                tag === MATH_SUPERSCRIPT_ARGUMENT_TAG ? SUPERSCRIPT_ALIGNMENT : tag === MATH_SUBSCRIPT_ARGUMENT_TAG ? SUBSCRIPT_ALIGNMENT : verticalAlignment;
            plainNodes.push(...WordOwnedTextUnlinker.plainNodesForMathContent(child, alignmentInsideChild));
        }
        return plainNodes;
    }

    /**
     * One ordinary run holding the characters of a math run, or null when the
     * run holds none. The run's `w:rPr` is copied so bold and italic survive;
     * its `m:rPr` is not, because it describes the equation's own styling,
     * which is gone with the equation.
     *
     * NULL
     *   The math run carried only properties, so it contributes no characters
     *   and no run is written for it.
     */
    private static plainRunFor(mathRun: Element, verticalAlignment: MathVerticalAlignment | null): Element | null {
        const ownerDocument = mathRun.ownerDocument as Document;
        const run = ownerDocument.createElementNS(WORDPROCESSINGML_NS, "w:r");
        const contentNodes: Element[] = [];
        for (const child of Array.from(mathRun.children)) {
            const tag = elementNameWithoutPrefix(child);
            if (TEXT_TAGS.has(tag)) {
                const textElement = ownerDocument.createElementNS(WORDPROCESSINGML_NS, `w:${tag}`);
                textElement.setAttributeNS(XML_NS, "xml:space", "preserve");
                textElement.textContent = child.textContent ?? "";
                contentNodes.push(textElement);
                continue;
            }
            /**
             * WHEN the child stands for one character rather than holding it (`w:tab`, `w:br`)
             *   CONSEQUENCE
             *     write the same element, so the character it stands for is still there.
             */
            if (isGlyphTag(tag)) contentNodes.push(ownerDocument.createElementNS(WORDPROCESSINGML_NS, `w:${tag}`));
        }
        if (contentNodes.length === 0) return null;
        const runProperties = WordOwnedTextUnlinker.wordRunPropertiesOf(mathRun, verticalAlignment);
        if (runProperties) run.appendChild(runProperties);
        for (const contentNode of contentNodes) run.appendChild(contentNode);
        return run;
    }

    /**
     * The `w:rPr` the flattened run should carry: the math run's own `w:rPr`
     * where it has one, with `w:vertAlign` set for a raised or lowered
     * argument. Any `w:vertAlign` already on the run is replaced, so the
     * equation's own raising wins over a copied one and never appears twice.
     *
     * NULL
     *   The run had no `w:rPr` and sits at the equation's baseline, so it needs
     *   no properties at all.
     */
    private static wordRunPropertiesOf(mathRun: Element, verticalAlignment: MathVerticalAlignment | null): Element | null {
        const ownerDocument = mathRun.ownerDocument as Document;
        const sourceProperties = Array.from(mathRun.children).find(
            (child) => elementNameWithoutPrefix(child) === "rPr" && !WordOwnedTextUnlinker.isMathSpelling(child),
        );
        if (!sourceProperties && !verticalAlignment) return null;
        const properties = sourceProperties
            ? (sourceProperties.cloneNode(true) as Element)
            : ownerDocument.createElementNS(WORDPROCESSINGML_NS, "w:rPr");
        if (!verticalAlignment) return properties;
        for (const child of Array.from(properties.children)) {
            if (elementNameWithoutPrefix(child) === "vertAlign") properties.removeChild(child);
        }
        const verticalAlignmentElement = ownerDocument.createElementNS(WORDPROCESSINGML_NS, "w:vertAlign");
        verticalAlignmentElement.setAttributeNS(WORDPROCESSINGML_NS, "w:val", verticalAlignment);
        properties.appendChild(verticalAlignmentElement);
        return properties;
    }

    /**
     * The same element with its attributes, no children: how a revision
     * container inside an equation is carried over to the flattened runs,
     * keeping its author, date and id.
     */
    private static shallowWordCopyOf(element: Element): Element {
        const ownerDocument = element.ownerDocument as Document;
        const copy = ownerDocument.createElementNS(WORDPROCESSINGML_NS, `w:${elementNameWithoutPrefix(element)}`);
        for (const attribute of Array.from(element.attributes)) copy.setAttributeNS(attribute.namespaceURI, attribute.name, attribute.value);
        return copy;
    }

    /**
     * Whether the element is written in the math namespace rather than the
     * wordprocessing one. `m:rPr` and `w:rPr` both read as `rPr` once the
     * prefix is dropped, and a paragraph parsed on its own carries no namespace
     * declarations, so the prefix is read from the name as well.
     */
    private static isMathSpelling(element: Element): boolean {
        return element.nodeName.startsWith("m:");
    }
}

/**
 * The one walk over a paragraph's complex fields, in the flat spelling Word
 * writes for DATE, REF, PAGEREF, HYPERLINK and table-of-contents entries: a run
 * sequence `fldChar begin` / instruction runs / `fldChar separate` / result runs
 * / `fldChar end` with no container around it. One stack, one reading of what
 * each marker means, read by the paragraph model, the unlinker, the link
 * striker and the splicer's restore of struck runs.
 *
 * CASES, per run in document order (`WordOwnedTextUnlinker.runsInDocumentOrder`)
 *   - `fldChar begin`                 -> opens a field: the run is its first run and its first marker, and
 *                                        a run of every field already open
 *   - an instruction run              -> a marker of the innermost open field; its words join that field's
 *                                        instruction while the field is before its `separate`
 *   - `fldChar separate`              -> a marker of the innermost open field, whose result starts after it
 *   - `fldChar end`                   -> the innermost open field's last marker: the field is complete and
 *                                        closes. It is a run of every open field and a result run of the
 *                                        outer ones, never of the result it closes
 *   - `fldChar end` with none open    -> the tail of a field begun in an earlier paragraph: a field of its
 *                                        own holding that one marker, never complete, open over no run
 *   - any other run while fields open -> a run of every open field, and of the result of each one past
 *                                        its `separate`
 *   - a run outside every field       -> the paragraph's own text: absent from the maps
 *
 * EDGE CASE, a run holding a marker and text together
 *   Word writes a marker in a run of its own; a run that also holds text is read
 *   in the state its marker leaves: text after a `begin` is in the field's
 *   instruction part, text after a `separate` in its result, text in an `end` run
 *   in the result that `end` closes.
 *
 * EXAMPLE
 *   a link inside a contents entry: [TOC begin] [instr ' TOC \o "1-3" \h '] [separate] [HYPERLINK begin]
 *   [instr ' HYPERLINK \l "_Toc1" '] [separate] ["1. Fees"] [end] [end]
 *   fields                 [the HYPERLINK (complete), the TOC (complete)], in the order their ends were met
 *   the TOC's resultRuns   the five runs of the HYPERLINK, begin to end
 *   the HYPERLINK's result ["1. Fees"]
 *   open over "1. Fees"    [{ the TOC, in its result }, { the HYPERLINK, in its result }]
 *
 * FALLBACK
 *   A paragraph with no `w:fldChar` holds no complex field: the empty walk, without
 *   reading its runs.
 */
export function complexFieldWalkOf(paragraph: Element): ComplexFieldWalk {
    const fields: ComplexField[] = [];
    const fieldsOpenOverRun = new Map<Element, FieldOpenOverRun[]>();
    const fieldOfMarkerRun = new Map<Element, ComplexField>();
    if (paragraph.getElementsByTagNameNS(WORDPROCESSINGML_NS, FIELD_CHAR_TAG).length === 0) return { fields, fieldsOpenOverRun, fieldOfMarkerRun };
    /** The fields open at the run being read, outermost first, each with whether it is past its `separate`. */
    const openFields: FieldOpenOverRun[] = [];
    /** Records the state the run's text is read in: a copy, since `inResult` changes as the walk goes on. */
    const recordFieldsOpenOver = (run: Element): void => {
        if (openFields.length > 0) fieldsOpenOverRun.set(run, openFields.map((openField) => ({ field: openField.field, inResult: openField.inResult })));
    };
    /** Adds the run to every open field, and to the result of each one past its `separate`. */
    const takeRunIntoOpenFields = (run: Element): void => {
        for (const openField of openFields) {
            openField.field.runsBeginToEnd.push(run);
            if (openField.inResult) openField.field.resultRuns.push(run);
        }
    };
    for (const run of WordOwnedTextUnlinker.runsInDocumentOrder(paragraph)) {
        const fieldCharType = WordOwnedTextUnlinker.fieldCharTypeOf(run);
        /**
         * EXAMPLE
         *   openFields = [the TOC, the HYPERLINK] -> `length - 1` is 1, the HYPERLINK, the innermost
         *   openFields = []                       -> `undefined`: no field is open
         */
        const innermostOpenField = openFields[openFields.length - 1];
        if (fieldCharType === FIELD_CHAR_TYPES.begin) {
            takeRunIntoOpenFields(run);
            const openedField: ComplexField = { runsBeginToEnd: [run], markerRuns: [run], resultRuns: [], fieldInstruction: "", complete: false };
            openFields.push({ field: openedField, inResult: false });
            fieldOfMarkerRun.set(run, openedField);
            recordFieldsOpenOver(run);
            continue;
        }
        if (fieldCharType === FIELD_CHAR_TYPES.end && innermostOpenField === undefined) {
            const tailOfEarlierParagraphsField: ComplexField = { runsBeginToEnd: [run], markerRuns: [run], resultRuns: [], fieldInstruction: "", complete: false };
            fields.push(tailOfEarlierParagraphsField);
            fieldOfMarkerRun.set(run, tailOfEarlierParagraphsField);
            continue;
        }
        /** WHEN no field is open CONSEQUENCE the run is the paragraph's own text, in no field. */
        if (innermostOpenField === undefined) continue;
        if (fieldCharType === FIELD_CHAR_TYPES.separate) {
            takeRunIntoOpenFields(run);
            innermostOpenField.field.markerRuns.push(run);
            innermostOpenField.inResult = true;
            fieldOfMarkerRun.set(run, innermostOpenField.field);
            recordFieldsOpenOver(run);
            continue;
        }
        if (fieldCharType === FIELD_CHAR_TYPES.end) {
            for (const openField of openFields) {
                openField.field.runsBeginToEnd.push(run);
                /** The end run closes the innermost field's result; it lies in the result of each outer field past its `separate`. */
                if (openField !== innermostOpenField && openField.inResult) openField.field.resultRuns.push(run);
            }
            innermostOpenField.field.markerRuns.push(run);
            innermostOpenField.field.complete = true;
            fieldOfMarkerRun.set(run, innermostOpenField.field);
            recordFieldsOpenOver(run);
            fields.push(innermostOpenField.field);
            openFields.pop();
            continue;
        }
        takeRunIntoOpenFields(run);
        if (WordOwnedTextUnlinker.holdsFieldInstruction(run)) {
            innermostOpenField.field.markerRuns.push(run);
            /**
             * CASES
             *   - the innermost field is before its `separate` -> the run's words are its instruction
             *   - it is past its `separate` (malformed markup) -> the words belong to no instruction
             */
            if (!innermostOpenField.inResult) innermostOpenField.field.fieldInstruction += run.textContent ?? "";
            fieldOfMarkerRun.set(run, innermostOpenField.field);
        }
        recordFieldsOpenOver(run);
    }
    /** The fields still open at the paragraph's end began here and end in a later paragraph. */
    for (const openField of openFields) fields.push(openField.field);
    return { fields, fieldsOpenOverRun, fieldOfMarkerRun };
}
